import React, { useState, useEffect } from "react";
import { Download, Zap, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { presets } from "../data/data";
import { motion } from "framer-motion";
import logo from "../assets/logo.png";

const Home = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkSize = () => setIsDesktop(window.innerWidth >= 1024);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const staggerContainer = {
    initial: {},
    animate: {
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const cardVariant = {
    initial: { opacity: 0, y: 40, rotate: -2 },
    animate: {
      opacity: 1,
      y: 0,
      rotate: -2,
      transition: { duration: 0.8 },
    },
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E7EB] font-sans selection:bg-[#00FF88] selection:text-black overflow-x-hidden relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.25, 0.45, 0.25] }}
          transition={{ duration: 9, repeat: Infinity }}
          className="absolute -top-[15%] -right-[15%] w-[75%] h-[75%] bg-[#00FF88]/15 blur-[190px] rounded-full"
        />
        <motion.div
          animate={{ scale: [1.15, 1, 1.15], opacity: [0.2, 0.35, 0.2] }}
          transition={{ duration: 11, repeat: Infinity }}
          className="absolute top-[40%] -left-[15%] w-[65%] h-[65%] bg-[#00FF88]/12 blur-[170px] rounded-full"
        />
      </div>

      <nav className="fixed w-full z-50 px-6 md:px-16 py-5 flex justify-between items-center backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="flex items-center gap-3 group cursor-pointer">
          <div className="w-9 h-9 flex items-center justify-center relative">
            <div className="absolute inset-0 bg-[#00FF88]/20 blur-md rounded-full group-hover:bg-[#00FF88]/40 transition-all"></div>
            <img
              src={logo}
              alt="Logo"
              className="w-full h-full object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6"
            />
          </div>
          <span className="font-black text-xl tracking-tighter uppercase italic text-white leading-none">
            Minty{" "}
            <span className="text-[#00FF88] drop-shadow-[0_0_15px_rgba(0,255,136,0.6)]">
              AI
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#00FF88] text-[7px] min-[380px]:text-[9px] font-black tracking-tight min-[380px]:tracking-[0.2em] uppercase italic bg-[#00FF88]/10 px-1.5 py-0.5 min-[380px]:px-3 min-[380px]:py-1 rounded-full border border-[#00FF88]/20 whitespace-nowrap">
            Upcoming on Playstore
          </span>
          <div className="w-1.5 h-1.5 bg-[#00FF88] rounded-full animate-pulse"></div>
        </div>
      </nav>

      <section className="min-h-screen flex items-center justify-center px-6 md:px-20 relative pt-16">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto text-center relative z-10 w-full"
        >
          <motion.div
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 mb-6 rounded-lg border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88] text-[9px] font-black tracking-[0.5em] uppercase"
          >
            Curated Collection
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="text-5xl sm:text-7xl md:text-9xl lg:text-[110px] font-black mb-8 tracking-tighter leading-[0.9] italic uppercase text-white"
          >
            AI{" "}
            <span className="text-transparent stroke-text opacity-90 mx-2 md:mx-4 tracking-normal">
              PROMPT
            </span>
            <br />
            <span className="text-[#00FF88] drop-shadow-[0_0_40px_rgba(0,255,136,0.4)]">
              PRESETS.
            </span>
          </motion.h1>

          <motion.div
            variants={fadeInUp}
            className="max-w-2xl mx-auto mb-10 text-center"
          >
            <p className="text-white text-lg sm:text-xl md:text-2xl font-black uppercase italic tracking-tighter leading-tight">
              Copy and use in{" "}
              <span className="text-[#00FF88] drop-shadow-[0_0_15px_rgba(0,255,136,0.5)]">
                just
              </span>{" "}
              one tap.
            </p>
          </motion.div>

          <motion.button
            variants={fadeInUp}
            onClick={() => {
              window.location.href =
                "https://github.com/reynaldomimis/sk-projects/releases/download/V1/mintyai.apk";
            }}
            className="group relative bg-[#00FF88] text-black px-12 py-5 rounded-2xl font-black text-lg md:text-xl shadow-[0_0_50px_-10px_rgba(0,255,136,0.6)] hover:shadow-[0_0_70px_rgba(0,255,136,0.8)] hover:scale-105 active:scale-95 transition-all flex items-center gap-4 uppercase italic tracking-tighter mx-auto overflow-hidden"
          >
            <div className="absolute inset-0 w-1/2 h-full bg-white/30 -skew-x-[45deg] -translate-x-[200%] group-hover:translate-x-[300%] transition-transform duration-1000"></div>
            <Download
              size={24}
              strokeWidth={3}
              className="group-hover:animate-bounce"
            />
            DOWNLOAD NOW
          </motion.button>
        </motion.div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-20 bg-[#0D0D0D]/50 border-y border-white/5 relative backdrop-blur-3xl">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8 border-l-4 border-[#00FF88] pl-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-black italic uppercase text-white leading-none tracking-tighter mb-4">
                User Presets
              </h2>
              <p className="text-gray-500 text-sm font-bold uppercase tracking-widest italic">
                Visual samples from the library
              </p>
            </div>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10 perspective-1000"
          >
            {presets.map((item, idx) => (
              <motion.div
                key={idx}
                variants={cardVariant}
                whileHover={isDesktop ? { rotate: 0, y: -16, scale: 1.02 } : {}}
                className="group relative aspect-[2/3] md:aspect-auto md:h-[440px] w-full transition-all duration-700"
              >
                <div className="absolute inset-0 bg-[#00FF88]/20 blur-[50px] opacity-0 lg:group-hover:opacity-100 transition-all duration-500 rounded-[2.5rem]" />

                <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden border border-white/10 bg-[#111] z-10 group-hover:border-[#00FF88]/60 transition-all shadow-2xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-all duration-1000 grayscale-0 opacity-95 scale-100 lg:grayscale lg:opacity-40 lg:scale-110 lg:group-hover:grayscale-0 lg:group-hover:opacity-100 lg:group-hover:scale-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent opacity-80" />
                </div>

                <div className="absolute inset-0 p-6 flex flex-col justify-end z-20">
                  <h3 className="text-xl md:text-2xl font-black text-[#00FF88] uppercase italic tracking-tighter mb-3 leading-tight">
                    {item.title}
                  </h3>

                  <div className="mb-5 bg-black/80 backdrop-blur-md px-4 py-4 md:px-5 md:py-5 rounded-2xl border border-white/10 text-gray-200 text-[10px] md:text-[11px] font-mono italic border-l-4 border-[#00FF88] shadow-xl">
                    <p className="leading-normal line-clamp-4 m-0 p-0">
                      "{item.prompt}"
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-white/50 font-black text-[9px] uppercase tracking-widest opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:translate-y-3 lg:group-hover:translate-y-0 transition-all">
                    <Zap
                      size={14}
                      fill="currentColor"
                      className="text-[#00FF88]"
                    />
                    Unlock in App
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-16 flex justify-center">
            <motion.button
              whileHover={{
                scale: 1.05,
                borderColor: "rgba(0, 255, 136, 0.5)",
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.location.href =
                  "https://github.com/reynaldomimis/sk-projects/releases/download/V1/mintyai.apk";
              }}
              className="relative px-8 py-4 rounded-xl border border-[#00FF88]/20 bg-[#00FF88]/5 text-[#00FF88] text-[11px] md:text-sm font-black uppercase tracking-[0.2em] italic transition-all cursor-pointer overflow-hidden group"
            >
              <div className="absolute inset-0 bg-[#00FF88]/10 blur-xl opacity-0 group-hover:opacity-40 transition-opacity"></div>

              <div className="relative z-10 flex items-center gap-3">
                <span className="drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                  More presets available in the app
                </span>
                <Download
                  size={16}
                  strokeWidth={3}
                  className="animate-bounce"
                />
              </div>

              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </motion.button>
          </div>
        </div>
      </section>

      <footer className="py-20 px-8 md:px-20 relative bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="mb-10">
            <Link to="/legal">
              <button className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-xl border border-white/5 bg-white/5 text-gray-400 hover:text-[#00FF88] transition-all">
                <ShieldCheck
                  size={18}
                  className="group-hover:rotate-12 transition-transform text-[#00FF88]"
                />
                <div className="flex flex-col items-start leading-none uppercase">
                  <span className="text-[9px] font-black tracking-[0.2em] italic">
                    Legal Information
                  </span>
                  <span className="text-[11px] font-bold opacity-60">
                    Privacy & Terms
                  </span>
                </div>
              </button>
            </Link>
          </div>
          <p className="text-[#00FF88] text-[12px] font-black tracking-[0.2em] uppercase italic opacity-70">
            © 2026 Upreyvan. All rights reserved.
          </p>
        </div>
      </footer>

      <style>{`
        .stroke-text { -webkit-text-stroke: 1.5px rgba(255,255,255,0.2); color: transparent; }
        @media (min-width: 1024px) { .stroke-text { -webkit-text-stroke: 2px rgba(255,255,255,0.2); } }
        .perspective-1000 { perspective: 1000px; }
      `}</style>
    </div>
  );
};

export default Home;
